/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, Select } from "core/components";
import { navigateTo } from "core/navigation";
import { CostItemFormData, CostLogFormData, CostLogItemFormData, CostLogSchema, CostLogStatus } from "core/schema";
import {
  admin_cost_logs_path,
  admin_cost_log_path,
  approve_admin_cost_log_path,
  create_for_approval_admin_cost_logs_path,
  reject_admin_cost_log_path,
  for_approval_admin_cost_log_path,
  new_admin_cost_log_path,
  new_change_order_admin_cost_logs_path,
  cancel_admin_cost_log_path
} from "routes";
import CostLogItemsForm from "./CostLogItemsForm";
import CostLogContext from "./CostLogContext";

type CostLogFormProps = {
  cost_log: CostLogFormData;
  cost_items: Array<CostItemFormData>;
};

const CostLogForm = ({ cost_log, cost_items }: CostLogFormProps): JSX.Element => {
  const [costLog, setCostLog] = React.useState(cost_log);
  const [loading, setLoading] = React.useState(false);
  const formMethods = useForm<CostLogFormData>({
    mode: "onChange",
    defaultValues: CostLogSchema.cast(costLog),
    resolver: yupResolver(CostLogSchema)
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, isValidating, errors }
  } = formMethods;
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [itemsForDestruction, setItemsForDestruction] = React.useState<Array<CostLogItemFormData>>([]);
  const isChangeOrderForm = location.href.includes("new_change_order");

  const documentDate = costLog.id ? moment(costLog.created_at).format("MMMM D, YYYY") : moment(new Date()).format("MMMM D, YYYY");
  const { policy } = costLog;

  const withItemsForDestruction = (data) => {
    const { cost_log_items_attributes } = data;

    return { ...data, cost_log_items_attributes: [...cost_log_items_attributes, ...itemsForDestruction] };
  };

  const save = async (data: CostLogFormData) => {
    try {
      if (data.id) {
        await api.put(admin_cost_log_path({ id: costLog.id }), withItemsForDestruction(data));
      } else {
        await api.post(admin_cost_logs_path({ id: costLog.id }), withItemsForDestruction(data));
      }

      navigateTo(admin_cost_logs_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const saveForApproval = async (data: CostLogFormData) => {
    try {
      if (data.id) {
        await api.put(for_approval_admin_cost_log_path({ id: costLog.id }), withItemsForDestruction(data));
      } else {
        await api.post(create_for_approval_admin_cost_logs_path({ id: costLog.id }), withItemsForDestruction(data));
      }
      navigateTo(admin_cost_logs_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const approve = async () => {
    try {
      await api.put(approve_admin_cost_log_path(costLog.id));
      navigateTo(admin_cost_logs_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const reject = async () => {
    try {
      await api.put(reject_admin_cost_log_path(costLog.id));
      navigateTo(admin_cost_logs_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const cancel = async () => {
    try {
      await api.put(cancel_admin_cost_log_path(costLog.id));
      navigateTo(admin_cost_logs_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const newCostLog = async (projectId) => {
    try {
      setLoading(true);
      const response = isChangeOrderForm
        ? await api.get(new_change_order_admin_cost_logs_path({ project_id: projectId }))
        : await api.get(new_admin_cost_log_path({ project_id: projectId }));
      setCostLog(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Items...</span>
        </Spinner>
      </div>
    );
  };

  const onError = (data) => {
    console.error(data);
    if (data?.cost_log_items_attributes) {
      setFormErrors([data.cost_log_items_attributes.message]);
    } else {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const actionsForDraft = () => {
    return (
      <>
        <Button
          className="button--brand mr-3"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="save"
          title="Save as Draft"
          onClick={() => {
            setValue("status", CostLogStatus.Draft);
            handleSubmit(async (data) => await save(data), onError)();
          }}
        />
        <Button
          className="button--brand mr-3"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="check"
          title="Submit for Approval"
          onClick={() => {
            setValue("status", CostLogStatus.ForApproval);
            handleSubmit(async (data) => await saveForApproval(data), onError)();
          }}
        />
        {costLog.id && (
          <Button
            className="button--danger"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="cancel"
            title="Cancel"
            onClick={handleSubmit(async (_) => await cancel(), onError)}
          />
        )}
      </>
    );
  };

  const actionsForApproval = () => {
    return (
      <>
        <Button
          className="button--brand mr-3"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="check-circle"
          title="Approve"
          onClick={handleSubmit(async (_) => await approve(), onError)}
        />
        <Button
          className="button--danger"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="times-circle"
          title="Reject"
          onClick={handleSubmit(async (_) => await reject(), onError)}
        />
      </>
    );
  };

  React.useEffect(() => {
    reset({ id: costLog.id, project_id: costLog.project_id, source: costLog.source, cost_log_items_attributes: costLog.cost_log_items_attributes });
  }, [costLog]);

  return (
    <React.StrictMode>
      <CostLogContext.Provider value={{ setItemsForDestruction }}>
        <FormProvider {...formMethods}>
          <Form errors={formErrors}>
            <Card>
              <Card.Header title={costLog.id ? `Cost Log` : `New Cost Log`}>
                <Button
                  icon="arrow-left"
                  iconProps={{ className: "mr-3" }}
                  className="button--default mr-3"
                  disabled={isSubmitting}
                  title="Back"
                  onClick={() => navigateTo(admin_cost_logs_path())}
                />
                {policy?.for_approval && actionsForDraft()}
                {policy?.approve && actionsForApproval()}
              </Card.Header>
              <Card.Body>
                <Container>
                  <Row>
                    <Col lg={6} className="px-4">
                      <Controller
                        name="project_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Project"
                            placeholder="Select Project"
                            required
                            disabled={!!costLog.id}
                            options={cost_log.projects}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            getOptionValue={(option) => option.id}
                            onChange={(newOption) => {
                              field.onChange(newOption?.id);
                              if (newOption?.id) newCostLog(newOption.id);
                              else {
                                setCostLog({ ...costLog, project_id: "", cost_log_items_attributes: [] });
                                setValue("code", "");
                              }
                            }}
                            value={cost_log.projects.find((project) => project.id == field.value) || null}
                            errorMessage={errors.project_id?.message}
                          />
                        )}
                      />
                      {costLog.id && <Input {...register("code")} readOnly label="Cost ID" required />}
                    </Col>
                    <Col lg={6} className="px-4">
                      <Input readOnly label="Document Date" value={documentDate} />
                    </Col>
                  </Row>
                </Container>
              </Card.Body>
            </Card>
            {loading
              ? loader()
              : costLog.project_id && (
                  <CostLogItemsForm
                    items={costLog.cost_log_items_attributes}
                    cost_items={cost_items}
                    task={costLog.task}
                    costLogStatus={costLog.status}
                  />
                )}
          </Form>
        </FormProvider>
      </CostLogContext.Provider>
    </React.StrictMode>
  );
};

export default CostLogForm;
